/**
 * <rilens-lens-facets> — mobile drawer shell for the "Tutte le lenti" facet sidebar.
 *
 * Owns the drawer — open, close, focus trap, scroll lock, focus restore — and keeping the
 * pill panel in step with the result set (see renderPanel below). The filtering itself —
 * fetch, product-grid swap, history push, popstate — stays in assets/facets.js, which
 * drives the same <facet-filters-form> this wraps.
 *
 * On desktop the sidebar is always visible and this element does nothing; the drawer
 * affordances are hidden by CSS, so the button below the breakpoint is the only entry
 * point. Deliberately NOT reusing the theme's own facets drawer: that path keys off the
 * `facets--desktop` class on <facet-filters-form> and would fight this layout.
 *
 * Markup contract (see sections/main-collection-lenses.liquid):
 *   <rilens-lens-facets>
 *     <button data-rcat-open aria-controls="RcatFacetsDrawer">
 *     <facet-filters-form><form><aside id="RcatFacetsDrawer" role="dialog">
 *       <button data-rcat-close>
 *     <div data-rcat-close>            the scrim
 */
(function () {
  if (customElements.get('rilens-lens-facets')) return;

  const OPEN_CLASS = 'rcat--drawer-open';
  const PANEL = '[data-rcat-facets-body]';

  /**
   * Swaps the pill panel from a section response.
   *
   * facets.js refreshes a filter UI group by group, matching `.js-filter[data-index]`
   * wrappers. This panel has none — a group appears and disappears with the results (rule
   * 3 in snippets/rilens-lens-facets.liquid), so there is no stable index to match — and
   * without them nothing in here was ever refreshed after the first page load: "Azzera"
   * stayed the muted, unclickable span, the counts went stale and "Filtra (n)" never moved.
   *
   * @param {string} html - Section Rendering API response
   * @param {Event} [event] - The input event that asked for it; absent on reset/popstate
   * @returns {boolean} false when this is not the Rilens panel
   */
  function renderPanel(html, event) {
    const panel = document.querySelector(PANEL);
    if (!panel) return false;

    const source = new DOMParser().parseFromString(html, 'text/html');
    const sourcePanel = source.querySelector(PANEL);
    if (!sourcePanel) return false;

    // A pill ticked while this response was in flight is not in it, and its own request is
    // still waiting behind the debounce to read the form — so on a user-driven render the
    // ticks on screen win. On reset and back/forward the response is the truth.
    const ticks = event
      ? [...panel.querySelectorAll('.rcat__pill-input')].map((input) => [input.id, input.checked])
      : [];
    const focusedId = panel.contains(document.activeElement) ? document.activeElement.id : '';

    panel.innerHTML = sourcePanel.innerHTML;

    ticks.forEach(([id, checked]) => {
      const input = document.getElementById(id);
      if (input && !input.disabled) input.checked = checked;
    });

    const opener = document.querySelector('[data-rcat-open]');
    const sourceOpener = source.querySelector('[data-rcat-open]');
    if (opener && sourceOpener) opener.innerHTML = sourceOpener.innerHTML;

    // The swap replaced whatever had focus, and the trap's first/last nodes with it.
    const drawer = document.getElementById('RcatFacetsDrawer');
    const refocus = (focusedId && document.getElementById(focusedId)) || null;
    if (drawer && document.documentElement.classList.contains(OPEN_CLASS) && typeof trapFocus === 'function') {
      trapFocus(drawer, refocus || drawer);
    } else if (refocus) {
      refocus.focus({ preventScroll: true });
    }

    return true;
  }

  // facets.js loads first, but do not depend on it: whenDefined resolves either way round.
  customElements.whenDefined('facet-filters-form').then(() => {
    const FacetForm = customElements.get('facet-filters-form');
    const stockRenderFilters = FacetForm.renderFilters;
    // On this layout the stock method has nothing to match and ends by reading
    // #FacetsWrapperDesktop, which does not exist here — so it is skipped, not chained.
    FacetForm.renderFilters = function (html, event) {
      if (renderPanel(html, event)) return;
      stockRenderFilters.call(this, html, event);
    };
  });

  class RilensLensFacets extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('#RcatFacetsDrawer');
      this.opener = this.querySelector('[data-rcat-open]');
      this.scrim = this.querySelector('.rcat__drawer-scrim');
      if (!this.drawer || !this.opener) return;

      this.open = this.open.bind(this);
      this.close = this.close.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);

      this.opener.addEventListener('click', this.open);
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-rcat-close]')) {
          event.preventDefault();
          this.close();
          return;
        }

        // "Azzera": reset in place, so the pills visibly clear and the drawer stays open.
        // Without facets.js the link is left alone and simply navigates.
        const reset = event.target.closest('[data-rcat-reset]');
        const FacetForm = customElements.get('facet-filters-form');
        if (reset && FacetForm) {
          event.preventDefault();
          FacetForm.renderPage(new URL(reset.href).searchParams.toString());
        }
      });
    }

    disconnectedCallback() {
      // The Section Rendering API can replace this element mid-interaction; make sure a
      // teardown never leaves the page scroll-locked or the Escape handler dangling.
      document.removeEventListener('keydown', this.onKeyDown);
      document.body.classList.remove('overflow-hidden');
      document.documentElement.classList.remove(OPEN_CLASS);
    }

    open() {
      document.documentElement.classList.add(OPEN_CLASS);
      document.body.classList.add('overflow-hidden');
      this.opener.setAttribute('aria-expanded', 'true');
      if (this.scrim) this.scrim.hidden = false;
      document.addEventListener('keydown', this.onKeyDown);

      // trapFocus/removeTrapFocus are the theme's own helpers from assets/global.js.
      if (typeof trapFocus === 'function') {
        trapFocus(this.drawer, this.drawer);
      } else {
        this.drawer.focus();
      }
    }

    close() {
      if (!document.documentElement.classList.contains(OPEN_CLASS)) return;

      document.documentElement.classList.remove(OPEN_CLASS);
      document.body.classList.remove('overflow-hidden');
      this.opener.setAttribute('aria-expanded', 'false');
      if (this.scrim) this.scrim.hidden = true;
      document.removeEventListener('keydown', this.onKeyDown);

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.opener);
      } else {
        this.opener.focus();
      }
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    }
  }

  customElements.define('rilens-lens-facets', RilensLensFacets);
})();
